import type { CachedUser } from './coldOpenPlan'

export type { CachedUser }

export type SessionSnapshot = {
  token: string
  tokenPresent: boolean
  cachedUser: CachedUser | null
}

export type WriteCachedUserOptions = {
  expectedToken?: string
}

export type SessionDocumentPorts = {
  getToken: () => Promise<unknown>
  setToken: (value: string) => Promise<void>
  getUserRaw: () => Promise<string | null | undefined>
  setUserRaw: (value: string) => Promise<void>
}

export type SessionDocument = {
  read: () => Promise<SessionSnapshot>
  getToken: () => Promise<string>
  writeCachedUser: (user: CachedUser, options?: WriteCachedUserOptions) => Promise<boolean>
  drop: () => Promise<void>
  replaceToken: (token: string) => Promise<void>
}

export function parseCachedUser (
  json: string | null | undefined,
): CachedUser | null {
  if (typeof json !== 'string' || json === '') {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(json)
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }
    return parsed as CachedUser
  } catch {
    return null
  }
}

export function asToken (raw: unknown): string {
  if (typeof raw === 'string') {
    return raw
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'value' in raw) {
    const value = (raw as { value: unknown }).value
    return typeof value === 'string' ? value : ''
  }
  return ''
}

export function createSessionDocument (
  ports: SessionDocumentPorts,
): SessionDocument {
  async function getToken (): Promise<string> {
    return asToken(await ports.getToken())
  }

  async function read (): Promise<SessionSnapshot> {
    const token = await getToken()
    const raw = await ports.getUserRaw()
    return {
      token,
      tokenPresent: Boolean(token),
      cachedUser: parseCachedUser(raw),
    }
  }

  async function writeCachedUser (
    user: CachedUser,
    options?: WriteCachedUserOptions,
  ): Promise<boolean> {
    const token = await getToken()
    if (!token) {
      return false
    }
    if (options?.expectedToken !== undefined && options.expectedToken !== token) {
      return false
    }
    await ports.setUserRaw(JSON.stringify(user))
    return true
  }

  async function drop (): Promise<void> {
    await ports.setToken('')
    await ports.setUserRaw('')
  }

  async function replaceToken (token: string): Promise<void> {
    await ports.setUserRaw('')
    await ports.setToken(token)
  }

  return {
    read,
    getToken,
    writeCachedUser,
    drop,
    replaceToken,
  }
}
